import { useState, useEffect, useRef } from "react";
import useUniversalStore from "store/universalStore";
import { getAllTagsFromAccounts } from "utils";
import { splitByKhataType, reMapSharedAccounts, applySearch, applyTagFilter, applySort, applyHideClosed } from "./config";

const Filters = ({ setFilteredAccounts, KhataType, setLoading }) => {
  const { accounts } = useUniversalStore((state) => state);
  const [hideClosed, setHideClosed] = useState(true);
  const [sort, setSort] = useState({ col: "name", desc: false });
  const [search, setSearch] = useState("");
  const [selectedTag, setSelectedTag] = useState(null);
  const [tags, setTags] = useState([]);
  // Cache shared-account user info so KYC lookups only happen once per user
  const sharedInfoCache = useRef({});

  // Recompute available tags whenever the full account list changes
  useEffect(() => {
    if (accounts) setTags(getAllTagsFromAccounts(accounts));
  }, [accounts]);

  // Main filter pipeline – re-runs whenever any filter input changes
  useEffect(() => {
    const getFilteredAccounts = async () => {
      setLoading(true);
      if (!accounts) {
        setFilteredAccounts([]);
        setLoading(false);
        return;
      }

      // 1. Split by tab (own / shared) — never mutates the global store
      let result = splitByKhataType(accounts, KhataType);

      // 2. get shared accounts with owner info
      if (KhataType === "shared") {
        result = await reMapSharedAccounts(result, sharedInfoCache);
      }

      // 3. Apply search, tag, sort, hide-closed
      result = applySearch(result, search);
      result = applyTagFilter(result, selectedTag);
      result = applySort(result, sort);
      result = applyHideClosed(result, hideClosed);

      setFilteredAccounts(result);
      setLoading(false);
    };

    getFilteredAccounts();
  }, [accounts, KhataType, sort, hideClosed, search, selectedTag]);


  const handleSearchInput = (e) => {
    setSearch(e.target.value);
  }

  const clearSearch = () => setSearch("");

  const TagDropdown = () => {
    return (
      <div className="dropdown mx-2">
        <i
          className={`fs-4 bi bi-tag${selectedTag ? "-fill text-app" : ""}`}
          role="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        ></i>
        <ul className="dropdown-menu">
          <li
            className="dropdown-item d-flex"
            role="button"
            onClick={() => setSelectedTag(null)}
          >
            <span className="me-auto">All Tags</span>
            {!selectedTag && <i className="bi bi-check2"></i>}
          </li>
          <li>
            <hr className="dropdown-divider" />
          </li>
          {tags.map((tag, index) => (
            <li
              key={index}
              className="dropdown-item d-flex"
              role="button"
              onClick={() => setSelectedTag(tag)}
            >
              <span className="me-auto text-uppercase">{tag}</span>
              {selectedTag === tag && <i className="bi bi-check2"></i>}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const SortDropdown = () => {
    return (
      <div className="dropdown pt-1 px-2">
        <i
          className="bi bi-arrow-down-up dropdown-toggle fs-5"
          type="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        />
        <ul className="dropdown-menu">
          <li
            className="dropdown-item cursor-pointer"
            onClick={(e) => setSort({ col: "name", desc: !sort.desc })}
          >
            <span className="me-auto">Name</span>
            <span className={sort.col === "name" ? "" : "invisible"}>
              <i
                className={`bi ${sort.desc ? "bi-sort-alpha-down" : "bi bi-sort-alpha-up"
                  }`}
              ></i>
            </span>
          </li>
          <li
            className="dropdown-item cursor-pointer"
            onClick={(e) => setSort({ col: "balance", desc: !sort.desc })}
          >
            <span className="me-auto">Balance</span>
            <span className={sort.col === "balance" ? "" : "invisible"}>
              <i
                className={`bi ${sort.desc
                  ? "bi-sort-numeric-up-alt"
                  : "bi bi-sort-numeric-down"
                  }`}
              ></i>
            </span>
          </li>
        </ul>
      </div>
    );
  };

  return (
    <div className="row border shadow mx-0 px-2 py-3">
      <div className="col-9 px-2">
        <div className="input-group w-auto">
          {!search && <span className="input-group-text">
            <i className="bi bi-search"></i>
          </span>}
          <input
            type="text"
            className="form-control shadow-none py-0 col-8 col-md-8 accountsearch text-start border border-bottom"
            placeholder="Search accounts"
            value={search}
            onChange={handleSearchInput}
          />
          {search && (
            <button
              className="btn btn-outline-secondary"
              onClick={clearSearch}
              type="button"
            >
              <i className="bi bi-x"></i>
            </button>
          )}
        </div>
      </div>
      <div className="col-3">
        <div className="d-flex align-items-center justify-content-center">
          <SortDropdown />
          <i className={`fs-4 mx-2 bi  ${hideClosed
            ? "bi-filter-circle-fill text-app" : "bi-filter"}`}
            role="button"
            onClick={(e) => setHideClosed(!hideClosed)}></i>
          <TagDropdown />
        </div>
      </div>
    </div>
  );
};

export default Filters;
